import styled from "@emotion/styled"
import { useDealerContext } from "@porsche-customer/slfinrtl-context"
import { PHeading, PSpinner } from "@porsche-design-system/components-react"
import { durationSm, easeInOut, spacingStaticLg } from "@porsche-design-system/components-react/emotion"
import type {
	InventoryListFilterValue,
	InventoryListItem,
	InventoryListSortingKey,
	InventoryListView,
	SortingValue,
} from "@slfinrtl/ui-bff-routes"
import {
	generateEnergyEfficiencyLabelRoute,
	getMonroneyLabelByInventoryIdRoute,
	vehicleShareLinkLimits,
} from "@slfinrtl/ui-bff-routes"
import { useCallback, useMemo } from "react"
import { defineMessages, FormattedMessage } from "react-intl"
import useSWRInfinite from "swr/infinite"
import { isUsingPcw20Website } from "src/common/getPcw20Host"
import { openPdf } from "src/common/pdf/openPdf"
import { trackEvent } from "src/components/adobeAnalytics"
import { ErrorNotificationInline } from "src/context/errorHandler/ErrorNotificationInline"
import { withErrorHandler } from "src/context/errorHandler/withErrorHandler"
import { useModal } from "src/context/modal/useModal"
import { ScrollRestore } from "src/context/scrollRestoration/ScrollRestore"
import type { ActionItemKey } from "src/hooks"
import { trackingActionMap, useBffDynamic, useConfig, useInfiniteScroll } from "src/hooks"
import { InventoryListRowActionMenuContainer } from "./InventoryListRowActionMenuContainer"
import { InventoryList } from "./components/inventoryList/InventoryList"
import { InventoryListSkeleton } from "./components/inventoryList/InventoryListSkeleton"
import { VehicleSharingBar } from "./components/vehicleSharingBar/VehicleSharingBar"
import { useFinderShareVehiclesUrl } from "./hooks/useFinderShareVehiclesUrl/useFinderShareVehiclesUrl"
import { useInventoryListView } from "./hooks/useInventoryListView/useInventoryListView"
import { useVehicleSelection } from "./hooks/useVehicleSelection/useVehicleSelection"
import { ShareVehiclesModalContainer } from "./modals/shareVehiclesModal/ShareVehiclesModalContainer"
import { ShareVehiclesWithMessageModalContainer } from "./modals/shareVehiclesWithMessageModal/ShareVehiclesWithMessageModalContainer"

const messages = defineMessages({
	permissionsHeadline: {
		defaultMessage: "Missing permissions",
		id: "inventorylist.missingPermissions.headline",
	},
	permissionsSubline: {
		defaultMessage: "You don't have access rights to see this inventory.",
		id: "inventorylist.missingPermissions.subline",
	},
})

const trackingKeyMap: Record<string, string> = {
	arrivalDate: "arrival_date",
	firstRegistrationDate: "first_registration_date",
	inventoryNumber: "inventory_number",
	lastPriceChange: "last_price_change",
	"mileage.value": "mileage",
	"modelInformation.modelYear": "model_year",
	"modelInformation.name": "model",
	"price.value": "price",
	salesStatus: "sales_status",
}

interface InventoryListScreenContainerProps {
	filter: InventoryListFilterValue
	hasLocationFilter: boolean
	onSortingChange: (val: SortingValue<InventoryListSortingKey>) => void
	sorting: SortingValue<InventoryListSortingKey>
}

export const InventoryListScreenContainer = withErrorHandler((props: InventoryListScreenContainerProps) => {
	const { filter, hasLocationFilter, onSortingChange, sorting } = props

	const { carSalesLiteEnabled, firstRegistrationDateAvailable } = useConfig()
	const { dealershipId } = useDealerContext()
	const fetchVehicles = useInventoryListView()

	const getEELLink = useBffDynamic(generateEnergyEfficiencyLabelRoute, "inline")

	const onEELClick = useCallback(
		(vehicleKey: string) =>
			getEELLink({
				body: undefined,
				pathParams: { inventoryId: vehicleKey },
				queryParams: {},
			}).then(({ document }) => {
				openPdf(document)
			}),
		[getEELLink],
	)

	const getMonroneyLabelUrl = useBffDynamic(getMonroneyLabelByInventoryIdRoute, "global")

	const onMonroneyLabelClick = useCallback(
		async (inventoryId: string) => {
			const monroneyLabelView = await getMonroneyLabelUrl({
				body: undefined,
				pathParams: { inventoryId },
				queryParams: {},
			})
			window.open(monroneyLabelView.downloadUrl, "_blank")
		},
		[getMonroneyLabelUrl],
	)

	const getKey = useCallback(
		(pageIndex: number) => [JSON.stringify({ ...filter, ...sorting }), pageIndex],
		[filter, sorting],
	)
	const { data, error, isLoading, isValidating, setSize } = useSWRInfinite<InventoryListView>(getKey, fetchVehicles, {
		keepPreviousData: true,
		parallel: true,
		persistSize: false,
		revalidateAll: true,
	})

	const handleSortingChange = useCallback(
		(key: InventoryListSortingKey) => {
			const sortDir = sorting.sortBy !== key ? "ASC" : sorting.sortDir === "DESC" ? "ASC" : "DESC"
			onSortingChange({
				sortBy: key,
				sortDir,
			})
			trackEvent({ action: `sort_by_${getAnalyticsAction(key)}_${sortDir.toLowerCase()}`, category: "inventory" })
		},
		[sorting, onSortingChange],
	)

	const currentView: InventoryListView | undefined = useMemo(() => {
		if (!data) {
			return
		}

		// Keep meta information (hasMore, total) of the last fetched Item only. Pick inventoryItems of all others
		return {
			...data[data.length - 1],
			items: data.flatMap(({ items }) => items),
		}
	}, [data])

	const {
		currentVehiclesSelectionState,
		onAllVehiclesSelectionClear,
		onCurrentVehiclesSelectionToggle,
		onVehicleSelectionToggle,
		selectedVehicles,
	} = useVehicleSelection(currentView?.items ?? [])

	useInfiniteScroll(() => {
		if (isValidating || currentView?.hasMore === false) {
			return
		}
		setSize((prev) => prev + 1)
	}, 1200)

	const onActionMenuItemClick = useCallback((itemKey: ActionItemKey) => {
		trackEvent({ action: trackingActionMap[itemKey], category: "inventory_action_menu" })
	}, [])

	const renderActionMenu = useCallback(
		(item: InventoryListItem) => (
			<InventoryListRowActionMenuContainer
				item={item}
				onActionMenuItemClick={onActionMenuItemClick}
				onEELClick={onEELClick}
				onMonroneyLabelClick={onMonroneyLabelClick}
			/>
		),
		[onActionMenuItemClick, onEELClick, onMonroneyLabelClick],
	)

	const onAddPriceClick = useCallback(() => {
		trackEvent({ action: "add_price", category: "inventory" })
	}, [])

	const onAddStockNumberClick = useCallback(() => {
		trackEvent({ action: "add_stock_number", category: "inventory" })
	}, [])

	const { customerFinderUrl } = useFinderShareVehiclesUrl({ selectedVehicles })

	const showShareVehiclesModal = useModal(ShareVehiclesModalContainer)
	const showShareVehiclesWithMessageModal = useModal(ShareVehiclesWithMessageModalContainer)

	const onShareVehiclesClick = useCallback(() => {
		if (isUsingPcw20Website(dealershipId)) {
			showShareVehiclesWithMessageModal({ listingIds: [...selectedVehicles] })
		} else {
			if (!customerFinderUrl) {
				return
			}
			showShareVehiclesModal({ count: selectedVehicles.size, customerFinderUrl })
		}
		trackEvent({ action: trackingActionMap.openShareVehiclesModal, category: "inventory_vehicle_sharing_bar" })
	}, [customerFinderUrl, dealershipId, selectedVehicles, showShareVehiclesModal, showShareVehiclesWithMessageModal])

	const onOpenVehiclesClick = useCallback(
		() =>
			trackEvent({ action: trackingActionMap.openShareVehiclesLink, category: "inventory_vehicle_sharing_bar" }),
		[],
	)

	return error?.response?.status === 403 ? (
		<ErrorWrapper>
			<PHeading align="center" size="lg">
				<FormattedMessage {...messages.permissionsHeadline} />
			</PHeading>
			<PHeading align="center" size="sm">
				<FormattedMessage {...messages.permissionsSubline} />
			</PHeading>
		</ErrorWrapper>
	) : (
		<>
			<ErrorNotificationInline />
			{currentView && !isLoading ? (
				<>
					<InventoryList
						displayLocation={hasLocationFilter && filter.dealershipIds?.length !== 1}
						isFirstRegistrationDateAvailable={firstRegistrationDateAvailable}
						isCarSalesLiteEnabled={carSalesLiteEnabled}
						view={currentView}
						onVehicleSelectionToggle={onVehicleSelectionToggle}
						onCurrentVehiclesSelectionToggle={onCurrentVehiclesSelectionToggle}
						currentVehiclesSelectionState={currentVehiclesSelectionState}
						renderActionMenu={renderActionMenu}
						selectedVehicles={selectedVehicles}
						onAddPriceClick={onAddPriceClick}
						onAddStockNumberClick={onAddStockNumberClick}
						sorting={sorting}
						onSortingChange={handleSortingChange}
					/>
					<ScrollRestore />
					{isValidating && (
						<SpinnerWrapper>
							<PSpinner />
						</SpinnerWrapper>
					)}
					{selectedVehicles.size > 0 && (
						<StyledVehicleSharingBar
							count={selectedVehicles.size}
							customerFinderUrl={customerFinderUrl}
							maxCount={vehicleShareLinkLimits.maxListings}
							onOpenVehiclesClick={onOpenVehiclesClick}
							onShareVehiclesClick={onShareVehiclesClick}
							onCloseClick={onAllVehiclesSelectionClear}
						/>
					)}
				</>
			) : (
				<InventoryListSkeleton
					isCarSalesLiteEnabled={carSalesLiteEnabled}
					isFirstRegistrationDateAvailable={firstRegistrationDateAvailable}
				/>
			)}
		</>
	)
})

const getAnalyticsAction = (key: string) => {
	if (key.startsWith("modelInformation.name")) {
		return trackingKeyMap["modelInformation.name"]
	}
	return trackingKeyMap[key]
}

const SpinnerWrapper = styled.div`
	display: flex;
	justify-content: center;
	position: absolute;
	inset-inline-start: 0;
	inset-inline-end: 0;
`

const ErrorWrapper = styled.div`
	margin-top: ${spacingStaticLg};
`

const StyledVehicleSharingBar = styled(VehicleSharingBar)`
	position: fixed;
	bottom: ${spacingStaticLg};
	left: 50%; /* inset-inline-start won't work with RTL because of transform */
	animation: slideInFromBottom ${durationSm} ${easeInOut} forwards;

	@keyframes slideInFromBottom {
		from {
			transform: translate3d(-50%, 100%, 0);
			opacity: 0;
		}
		to {
			transform: translate3d(-50%, 0, 0);
			opacity: 1;
		}
	}
`
